import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../utils/cn";
import type { AgendaWeekSummary } from "../agendaTypes";
import {
  getFortalezaTodayIso,
  normalizeAgendaIsoDate,
} from "../agendaFiltersQuery";
import { isoDateToDisplayDate, shiftIsoDate } from "../agendaDateFormat";
import {
  getAgendaWeekStripEndIso,
  getAgendaWeekStripStartIso,
} from "../agendaWeekDateRange";

type WeekStripCompactProps = {
  selectedDateIso: string;
  weekSummary: AgendaWeekSummary;
  onSelectDate: (isoDate: string) => void;
  onShiftWeek: (isoDate: string) => void;
  className?: string;
};

const WeekStripCompact: React.FC<WeekStripCompactProps> = ({
  selectedDateIso,
  weekSummary,
  onSelectDate,
  onShiftWeek,
  className,
}) => {
  const todayIso = getFortalezaTodayIso();
  const firstAllowedIso = getAgendaWeekStripStartIso(todayIso);
  const lastAllowedIso = getAgendaWeekStripEndIso(todayIso);
  const normalizedSelectedDate =
    normalizeAgendaIsoDate(selectedDateIso) ?? selectedDateIso;
  const isToday = normalizedSelectedDate === todayIso;
  const isFirstDay = normalizedSelectedDate <= firstAllowedIso;
  const isLastDay = normalizedSelectedDate === lastAllowedIso;

  const selectedDayMeta = weekSummary.days.find(
    (day) => (normalizeAgendaIsoDate(day.date) ?? day.date) === normalizedSelectedDate
  );

  const weekdayLabel = selectedDayMeta?.weekday_label ?? "—";
  const displayShort = isoDateToDisplayDate(normalizedSelectedDate).slice(0, 5);

  return (
    <section
      className={cn("flex items-center gap-1.5", className)}
      aria-label="Data selecionada"
    >
      <button
        type="button"
        disabled={isFirstDay}
        className={cn(
          "shrink-0 rounded-lg border border-[#E2E8F0] bg-white p-2 text-[#4A5568] hover:bg-[#F7FAFC]",
          isFirstDay && "cursor-not-allowed opacity-40"
        )}
        aria-label="Dia anterior"
        onClick={() => {
          if (!isFirstDay) {
            onShiftWeek(shiftIsoDate(normalizedSelectedDate, -1));
          }
        }}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>

      <button
        type="button"
        onClick={() => onSelectDate(normalizedSelectedDate)}
        className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-semibold text-[#2D3748]"
      >
        <span className="uppercase tracking-wide">{weekdayLabel}</span>
        <span>{displayShort}</span>
        {isToday ? (
          <span className="rounded-full bg-[#FFF5F0] px-2 py-0.5 text-[11px] font-semibold uppercase text-[#c4552b]">
            hoje
          </span>
        ) : normalizedSelectedDate < todayIso ? (
          <span className="rounded-full bg-[#EDF2F7] px-2 py-0.5 text-[11px] font-semibold uppercase text-[#718096]">
            ontem
          </span>
        ) : null}
      </button>

      <button
        type="button"
        disabled={isLastDay}
        className={cn(
          "shrink-0 rounded-lg border border-[#E2E8F0] bg-white p-2 text-[#4A5568] hover:bg-[#F7FAFC]",
          isLastDay && "cursor-not-allowed opacity-40"
        )}
        aria-label="Próximo dia"
        onClick={() => {
          if (!isLastDay) {
            onShiftWeek(shiftIsoDate(normalizedSelectedDate, 1));
          }
        }}
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </section>
  );
};

export default WeekStripCompact;
